import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Warning, UserPlus, Eye, EyeSlash } from '@phosphor-icons/react';

export default function RegisterPage() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName || !email || !password) { toast.error('Zorunlu alanları doldurun'); return; }
    if (password.length < 6) { toast.error('Şifre en az 6 karakter olmalı'); return; }
    setLoading(true);
    try {
      await register(fullName, email, password, phone || null);
      toast.success('Kayıt başarılı');
      navigate('/reporter');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kayıt başarısız');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-6 relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-15"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1759701058584-ea4bdce848c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxlbWVyZ2VuY3klMjBsaWdodHMlMjBibHVyfGVufDB8fHx8MTc3NDExODk2MHww&ixlib=rb-4.1.0&q=85')" }}
      />
      <div className="absolute inset-0 bg-[#0A0A0A]/80" />

      <div className="relative z-10 w-full max-w-md animate-slide-up">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <Warning weight="fill" className="w-8 h-8 text-[hsl(var(--primary))]" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tight">ACİL İHBAR</h1>
          <p className="overline text-[hsl(var(--muted-foreground))] mt-2">Yeni Hesap Oluştur</p>
        </div>

        <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-white font-heading">KAYIT OL</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="overline text-[hsl(var(--muted-foreground))] mb-1.5 block">Ad Soyad *</label>
                <Input
                  data-testid="register-name-input"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="Adiniz Soyadiniz"
                  className="h-14 bg-[#0A0A0A] border-[#2C2C2E] text-white placeholder:text-[#666]"
                />
              </div>
              <div>
                <label className="overline text-[hsl(var(--muted-foreground))] mb-1.5 block">E-Posta *</label>
                <Input
                  data-testid="register-email-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="ornek@email.com"
                  className="h-14 bg-[#0A0A0A] border-[#2C2C2E] text-white placeholder:text-[#666]"
                />
              </div>
              <div>
                <label className="overline text-[hsl(var(--muted-foreground))] mb-1.5 block">Sifre *</label>
                <div className="relative">
                  <Input
                    data-testid="register-password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="En az 6 karakter"
                    className="h-14 bg-[#0A0A0A] border-[#2C2C2E] text-white placeholder:text-[#666] pr-12"
                  />
                  <button type="button" data-testid="register-toggle-password" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-white transition-colors">
                    {showPassword ? <EyeSlash weight="bold" className="w-5 h-5" /> : <Eye weight="bold" className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="overline text-[hsl(var(--muted-foreground))] mb-1.5 block">Telefon</label>
                <Input
                  data-testid="register-phone-input"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+90 500 123 4567"
                  className="h-14 bg-[#0A0A0A] border-[#2C2C2E] text-white placeholder:text-[#666]"
                />
              </div>
              <Button
                data-testid="register-submit-button"
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white font-heading text-lg uppercase tracking-wide"
              >
                <UserPlus weight="bold" className="w-5 h-5 mr-2" />
                {loading ? 'Kaydediliyor...' : 'Kayıt Ol'}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/login" data-testid="login-link" className="text-sm text-[hsl(var(--muted-foreground))] hover:text-white transition-colors">
                Zaten hesabınız var mı? <span className="text-[hsl(var(--primary))] font-semibold">Giriş Yap</span>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
